"use client";

import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    FaFacebook,
    FaXTwitter,
    FaInstagram,
    FaLinkedin,
} from "react-icons/fa6";
import { Mail, MapPin, Phone, Send } from "lucide-react";
import { Link } from "react-router-dom";

const Footer = () => {
    const { t } = useTranslation("footer");
    const currentYear = new Date().getFullYear();

    const links = [
        { label: t("links.home"), href: "/" },
        { label: t("links.services"), href: "/services" },
        { label: t("links.candidates"), href: "/candidates" },
        { label: t("links.about"), href: "/about" },
        { label: t("links.contact"), href: "/contact" },
    ];

    const legal = [
        { label: t("legal.privacy"), href: "/privacy" },
        { label: t("legal.terms"), href: "/terms" },
        { label: t("legal.cookies"), href: "/cookies" },
    ];

    const contact = [
        {
            icon: Mail,
            label: "Email",
            value: "contact@tekhdem.dz",
            href: "mailto:contact@tekhdem.dz",
        },
        {
            icon: Phone,
            label: t("contact.phone"),
            value: "+213 555 123 456",
            href: "tel:+213555123456",
        },
        {
            icon: MapPin,
            label: t("contact.address"),
            value: "Algiers, Algeria",
            href: "#",
        },
    ];

    const socials = [
        { icon: FaFacebook, href: "#", label: "Facebook" },
        { icon: FaXTwitter, href: "#", label: "X (Twitter)" },
        { icon: FaInstagram, href: "#", label: "Instagram" },
        { icon: FaLinkedin, href: "#", label: "LinkedIn" },
    ];

    return (
        <footer className="relative bg-white dark:bg-gray-950 border-t border-gray-100 dark:border-gray-800 pt-20 pb-10 overflow-hidden transition-colors duration-300">

            {/* Subtle Background Glows */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-24 -left-24 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl" />
                <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Top Section: Grid Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">

                    {/* Brand & About (Col Span 4) */}
                    <div className="lg:col-span-4 space-y-6">
                        <Link to="/" className="flex items-center gap-2 group w-fit">
                            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/20 group-hover:scale-110 transition-transform duration-300">
                                <span className="font-bold text-white text-xl">T</span>
                            </div>
                            <span className="text-2xl font-bold text-gray-900 dark:text-white">
                                Tekhdem
                            </span>
                        </Link>
                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed max-w-sm">
                            {t("brand.description")}
                        </p>
                        <div className="flex gap-3">
                            {socials.map((social, i) => {
                                const Icon = social.icon;
                                return (
                                    <motion.a
                                        key={i}
                                        href={social.href}
                                        whileHover={{ y: -3, color: "#EAB308" }}
                                        className="w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/10 transition-colors border border-gray-200 dark:border-gray-800 hover:border-yellow-200 dark:hover:border-yellow-800"
                                        aria-label={social.label}
                                    >
                                        <Icon size={18} />
                                    </motion.a>
                                );
                            })}
                        </div>
                    </div>

                    {/* Quick Links (Col Span 2) */}
                    <div className="lg:col-span-2">
                        <h3 className="font-bold text-gray-900 dark:text-white mb-6">
                            {t("titles.quickLinks")}
                        </h3>
                        <ul className="space-y-4">
                            {links.map((link, i) => (
                                <li key={i}>
                                    <Link
                                        to={link.href}
                                        className="text-gray-500 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 text-sm font-medium transition-colors flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <span className="group-hover:translate-x-1 transition-transform">
                                            {link.label}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact (Col Span 3) */}
                    <div className="lg:col-span-3">
                        <h3 className="font-bold text-gray-900 dark:text-white mb-6">
                            {t("titles.contact")}
                        </h3>
                        <ul className="space-y-4">
                            {contact.map((item, i) => {
                                const Icon = item.icon;
                                return (
                                    <li key={i}>
                                        <a
                                            href={item.href}
                                            className="flex items-start gap-3 group"
                                        >
                                            <div className="mt-1 p-2 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-400 group-hover:text-yellow-500 group-hover:bg-yellow-50 dark:group-hover:bg-yellow-900/20 transition-colors">
                                                <Icon size={16} />
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
                                                    {item.label}
                                                </p>
                                                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors">
                                                    {item.value}
                                                </p>
                                            </div>
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Newsletter (Col Span 3) */}
                    <div className="lg:col-span-3">
                        <div className="p-6 rounded-2xl bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800">
                            <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                                {t("newsletter.title")}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
                                {t("newsletter.description")}
                            </p>
                            <div className="relative">
                                <input
                                    type="email"
                                    placeholder={t("newsletter.placeholder")}
                                    className="w-full pl-4 pr-12 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-yellow-500/20 focus:border-yellow-500 transition-all text-gray-900 dark:text-white placeholder:text-gray-400"
                                />
                                <button className="absolute right-1.5 top-1.5 p-1.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors shadow-lg shadow-yellow-500/20">
                                    <Send size={16} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-800 to-transparent mb-8" />

                {/* Bottom Bar */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <p>
                        © {currentYear} Tekhdem. {t("copyright")}
                    </p>

                    <div className="flex items-center gap-6">
                        {legal.map((item, i) => (
                            <Link
                                key={i}
                                to={item.href}
                                className="hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors"
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;